import * as R from 'ramda';

import {
  RouteDataState,
  RouteOptionsState,
} from '@atom/components/common/createAssetModal/CreateAssetModalContext';
import { Location } from '@atom/hooks/useCurrentLocation';
import {
  AssetCreateAttributesInput,
  AssetRouteAutocomplete,
  AssetRouteDataItem,
  AssetRouteSimpleInput,
} from '@atom/types/asset';
import { DataType } from '@atom/types/dataType';
import { Schema } from '@atom/types/schema';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

export const MILEPOST_TITLE = 'Milepost';

// Number components hold a numeric value instead of an enumerated selection.
export const isNumberComponent = (component: AssetRouteDataItem): boolean =>
  component?.dataType === DataType.NUMBER;

export const buildSimpleInput = (
  location: Partial<Location>,
  routeData: RouteDataState,
): AssetRouteSimpleInput => {
  return {
    geometry: location,
    components: R.toPairs(routeData).reduce(
      (acc, [title, value]) =>
        isNilOrEmpty(value) ? acc : [...acc, { title, value }],
      [],
    ),
  };
};

export const mapAutocompleteToOptions = (
  response: AssetRouteAutocomplete,
): RouteOptionsState => {
  return (response?.components || []).reduce(
    (acc, component) => ({ ...acc, [component.title]: component }),
    {} as RouteOptionsState,
  );
};

export const isMilePostValid = (
  milePost: number,
  ranges: number[][],
): boolean => {
  if (R.isNil(milePost) || isNilOrEmpty(ranges)) {
    return false;
  }

  return ranges.some(([start, end]) => milePost >= start && milePost <= end);
};

export const isRouteStepIncomplete = (
  routeData: RouteDataState,
  routeOptions: RouteOptionsState,
  milePost: number,
  milePostRanges: number[][],
  isRouteSnapped: boolean = false,
): boolean => {
  const titles = R.keys(routeOptions);
  const allComponentsFilled =
    titles.length > 0 && titles.every(title => !isNilOrEmpty(routeData[title]));

  // A snapped location means the server selected the milepost itself
  const isMilePostSettled =
    isRouteSnapped || isMilePostValid(milePost, milePostRanges);

  return !allComponentsFilled || !isMilePostSettled;
};

export const getRouteSubtext = (
  routeData: RouteDataState,
  routeOptions: RouteOptionsState,
  milePost: number,
): string => {
  const filled = R.reject(
    isNilOrEmpty,
    R.keys(routeOptions).map(title => routeData[title]),
  );

  return R.reject(isNilOrEmpty, [...filled, milePost]).join(' • ');
};

const getNumberUpdate = (
  current: RouteDataState[string],
  component: AssetRouteDataItem,
): RouteDataState => {
  const value = component.value ?? null;
  const isUnchanged = (R.isNil(current) && R.isNil(value)) || current === value;

  return isUnchanged ? {} : { [component.title]: value };
};

const getEnumerationUpdate = (
  current: RouteDataState[string],
  component: AssetRouteDataItem,
): RouteDataState => {
  const enumeration = component.enumeration || [];

  if (enumeration.length === 1 && current !== enumeration[0]) {
    return { [component.title]: enumeration[0] };
  }

  if (!isNilOrEmpty(current) && !enumeration.includes(current as string)) {
    return { [component.title]: null };
  }

  return {};
};

// Number components take the value the server returns. Enumerated
// components auto-select when narrowed to one option, and clear any value
// that is no longer part of the returned enumeration.
export const computeAutoFillUpdates = (
  routeData: RouteDataState,
  components: AssetRouteDataItem[],
): RouteDataState => {
  return (components || []).reduce((acc, component) => {
    const current = routeData[component.title];
    const update = isNumberComponent(component)
      ? getNumberUpdate(current, component)
      : getEnumerationUpdate(current, component);

    return { ...acc, ...update };
  }, {} as RouteDataState);
};

const buildAttributeNameToIdIndex = (
  schema: Schema,
): { [name: string]: string } => {
  return R.toPairs(schema?.attributes || {}).reduce((acc, [id, attribute]) => {
    return attribute?.name ? { ...acc, [attribute.name]: id } : acc;
  }, {});
};

// Map from the custom route data to the schema attribute data
export const mapRouteToAttributes = (
  schema: Schema,
  routeData: RouteDataState,
  milePost: number,
): AssetCreateAttributesInput => {
  const nameToId = buildAttributeNameToIdIndex(schema);
  const milePostId = nameToId[MILEPOST_TITLE];

  const componentEntries = R.toPairs(routeData).reduce(
    (acc, [title, value]) => {
      const id = nameToId[title];
      return id && !isNilOrEmpty(value) ? { ...acc, [id]: { value } } : acc;
    },
    {} as AssetCreateAttributesInput,
  );

  return milePostId && !R.isNil(milePost)
    ? { ...componentEntries, [milePostId]: { value: milePost } }
    : componentEntries;
};
